/**
 * DualFlagshipHero, FRONTEND-REBALANCE-1 hero.
 *
 * Statement Decoder and Invoice Checker are shown as co-equal flagships on
 * the homepage. Copy switches between caregiver and participant voice via
 * the PersonaToggle above the fold. Ask Wayly gets a tertiary conversational
 * entry at the bottom of the hero.
 *
 * Telemetry (client-side PostHog via `track`):
 *   - `hero_flagship_click`, { flagship: "statement_decoder" | "invoice_checker" }
 *   - `ask_wayly_home_click`
 */
import React, { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileSearch, ReceiptText, MessageCircle, Sparkles, MapPin, Scale, Info } from "lucide-react";
import PersonaToggle from "@/components/PersonaToggle";
import Reveal from "@/components/Reveal";
import HeroVideo from "@/components/HeroVideo";
import { track } from "@/lib/analytics";
import { BRAND_TAGLINE } from "@/lib/brand";

// Landing header tagline, sourced from the shared BRAND_TAGLINE constant so it
// stays identical to mobile (login + signup). Split into two tone-styled parts
// without altering the wording; CSS uppercases it to match mobile.
const TAGLINE_PARTS = BRAND_TAGLINE.split(", ");
const TAGLINE_HEAD = `${TAGLINE_PARTS[0]},`;
const TAGLINE_TAIL = TAGLINE_PARTS.slice(1).join(", ");

const COPY = {
    caregiver: {
        eyebrow: "Aged Care, Made Clear",
        headline: "Read the statement. Check the invoice. Pay with confidence.",
        subhead: "Wayly is the calm command centre for aged care: decode any statement or invoice, catch overcharges before you pay, track every dollar of funding, compare providers and get plain-English answers to anything, all in one place.",
        flagship_statement_sub: "Upload the monthly statement. Get a plain-English breakdown of every stream, every rate, every flag.",
        flagship_invoice_sub: "Upload the contribution invoice. Wayly runs the C1 to C12 rule engine and tells you what to raise before you pay.",
    },
    participant: {
        eyebrow: "Aged Care, Made Clear",
        headline: "Your Care. Your Statement. Your Call.",
        subhead: "Wayly puts you in control of your own care: understand every statement and invoice, see exactly where your funding goes, catch anything that looks off and get plain-English answers whenever you need them, all in one calm place.",
        flagship_statement_sub: "Drop in your monthly statement. See what every line means and where your quarterly budget stands.",
        flagship_invoice_sub: "Drop in an invoice from your provider. Wayly checks it against the rules and gives you the exact question to ask.",
    },
};

export default function DualFlagshipHero() {
    const [persona, setPersona] = useState("caregiver");
    const copy = COPY[persona] || COPY.caregiver;

    const onFlagshipClick = useCallback((flagship) => {
        try { track?.event?.("hero_flagship_click", { flagship, persona }); } catch { /* non-fatal */ }
    }, [persona]);

    return (
        <section
            className="relative overflow-hidden bg-gradient-to-br from-surface-2 via-surface to-surface-2 border-b border-kindred"
            data-testid="dual-flagship-hero"
        >
            <div className="pointer-events-none absolute inset-0 opacity-20 [background:radial-gradient(circle_at_20%_10%,#0F5648_0%,transparent_35%),radial-gradient(circle_at_80%_20%,#C99B2E_0%,transparent_30%)]" aria-hidden="true" />

            <div className="relative mx-auto max-w-7xl px-6 pt-14 pb-20 lg:pt-20 lg:pb-24">
                {/* Persona toggle above the fold */}
                <div className="flex items-center justify-end">
                    <PersonaToggle onChange={setPersona} defaultPersona={persona} />
                </div>

                <Reveal>
                    <h1
                        className="mt-3 font-heading font-black uppercase tracking-[-0.02em] leading-[0.95] text-[2rem] sm:text-4xl lg:text-5xl text-primary-k"
                        data-testid="hero-eyebrow"
                        style={{ textShadow: "0 1px 0 rgba(14,77,82,0.06)" }}
                    >
                        {TAGLINE_HEAD}{" "}
                        <span className="relative inline-block" style={{ color: "#A5512B" }}>
                            {TAGLINE_TAIL}
                        </span>
                    </h1>
                </Reveal>

                <Reveal delay={140}>
                    <p
                        className="mt-7 font-heading text-2xl sm:text-3xl lg:text-[1.9rem] xl:text-4xl text-primary-k tracking-tight leading-[1.12] max-w-none lg:whitespace-nowrap"
                        data-testid="hero-headline"
                    >
                        {copy.headline}
                    </p>
                    <p className="mt-5 text-lg text-primary-k/85 max-w-4xl leading-relaxed">
                        {copy.subhead}
                    </p>
                </Reveal>

                {/* Dual-flagship card row, Statement Decoder + Invoice Checker */}
                <div className="mt-10 grid lg:grid-cols-2 gap-5" data-testid="dual-flagship-cards">
                    <FlagshipCard
                        testid="hero-flagship-statement-decoder"
                        Icon={FileSearch}
                        eyebrow="Free"
                        title="Statement Decoder"
                        sub={copy.flagship_statement_sub}
                        note="1 free decode every 120 days. Unlimited with a Wayly plan."
                        cta="Decode a statement"
                        to="/ai-tools/statement-decoder"
                        onClick={() => onFlagshipClick("statement_decoder")}
                        accent="teal"
                    />
                    <FlagshipCard
                        testid="hero-flagship-invoice-checker"
                        Icon={ReceiptText}
                        eyebrow="7-day free trial"
                        title="Invoice Checker"
                        sub={copy.flagship_invoice_sub}
                        cta="Check an invoice"
                        to="/ai-tools/invoice-checker"
                        onClick={() => onFlagshipClick("invoice_checker")}
                        accent="clay"
                    />
                </div>

                {/* Trust strip: three promises presented as high-contrast pills with icons. */}
                <div
                    className="mt-10 flex flex-wrap items-center gap-2.5"
                    data-testid="hero-trust-strip"
                    role="list"
                    aria-label="What Wayly commits to"
                >
                    <span
                        role="listitem"
                        className="inline-flex items-center gap-2 rounded-full border border-primary-k/25 bg-white/80 backdrop-blur-sm px-3.5 py-1.5 text-[13px] font-medium text-primary-k shadow-sm"
                    >
                        <MapPin className="h-3.5 w-3.5 text-sage-700" aria-hidden="true" />
                        Data in Australia
                    </span>
                    <span
                        role="listitem"
                        className="inline-flex items-center gap-2 rounded-full border border-primary-k/25 bg-white/80 backdrop-blur-sm px-3.5 py-1.5 text-[13px] font-medium text-primary-k shadow-sm"
                    >
                        <Scale className="h-3.5 w-3.5 text-primary-k" aria-hidden="true" />
                        Independent, no provider ownership
                    </span>
                    <span
                        role="listitem"
                        className="inline-flex items-center gap-2 rounded-full border border-primary-k/25 bg-white/80 backdrop-blur-sm px-3.5 py-1.5 text-[13px] font-medium text-primary-k shadow-sm"
                    >
                        <Info className="h-3.5 w-3.5 text-clay-dark" aria-hidden="true" />
                        Information only, not financial or legal advice
                    </span>
                </div>

                {/* Hero product video, anchoring visual for the hero. */}
                <HeroVideo />
            </div>
        </section>
    );
}

function FlagshipCard({ testid, Icon, eyebrow, title, sub, cta, to, onClick, accent, badge, note }) {
    const accentClasses = accent === "clay"
        ? "border-clay/40 hover:border-clay hover:shadow-[0_20px_60px_rgba(181,124,87,0.25)]"
        : "border-sage/40 hover:border-primary-k hover:shadow-[0_20px_60px_rgba(15,86,72,0.20)]";
    const iconWrap = accent === "clay"
        ? "bg-clay/12 text-clay-dark"
        : "bg-sage/15 text-primary-k";
    return (
        <Link
            to={to}
            onClick={onClick}
            data-testid={testid}
            className={`group relative block rounded-2xl border-2 ${accent === "clay" ? "panel-clay" : "panel-teal"} p-6 lg:p-7 transition-colors ${accentClasses}`}
        >
            {badge && (
                <span className="absolute -top-2.5 right-6 inline-flex items-center gap-1 rounded-full bg-clay text-white text-[10px] font-medium uppercase tracking-wider px-2.5 py-1 shadow-md">
                    {badge}
                </span>
            )}
            <div className="flex items-start gap-4">
                <div className={`h-12 w-12 rounded-xl inline-flex items-center justify-center shrink-0 ${iconWrap}`}>
                    <Icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-white shadow-sm ${accent === "clay" ? "bg-[#A5512B]" : "bg-[#0E4D52]"}`}>
                        <Sparkles className="h-3 w-3" aria-hidden="true" /> {eyebrow}
                    </span>
                    <h2 className="mt-2.5 font-heading text-2xl text-primary-k tracking-tight">{title}</h2>
                    <p className="mt-2.5 text-sm text-primary-k/80 leading-relaxed">{sub}</p>
                    {note && <p className="mt-1.5 text-xs font-medium text-primary-k/70">{note}</p>}
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-k group-hover:gap-2 transition-colors">
                        {cta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                </div>
            </div>
        </Link>
    );
}
